import React, { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { errMsg } from '@/services/staffAttendanceApi';
import { tallyApi } from '@/services/ewayBillApi';

const VT = [['customer_invoice', 'Direct sales invoice →'], ['customer_dc', 'Customer DC →'], ['transfer_out', 'Transfer Out (other site) →'],
  ['branch_transfer', 'Within-site transfer →'], ['job_work', 'Job work DC →']];
const ACC = [['sales', 'Sales ledger'], ['cgst', 'CGST output ledger'], ['sgst', 'SGST output ledger'], ['igst', 'IGST output ledger'], ['round_off', 'Round off ledger (blank = none)']];
const NEW = [['party_group', 'Group for new customer ledgers'], ['stock_group', 'Stock group for new stock items'], ['unit', 'Unit for new stock items']];
const STEPS = [
  'On the Tally PC install Python 3 from python.org (tick “Add Python to PATH”).',
  'In TallyPrime: F1 Help → Settings → Connectivity → Client/Server configuration → TallyPrime acts as “Both”, Port 9000.',
  'Keep TallyPrime open with the company below loaded.',
  'Download the Tally link here, save it on the Tally PC and double-click it (or run “python wms_tally_link.py”). Keep its window open — put a shortcut in Windows Startup.',
  'WMS creates missing customer ledgers (“Name - Place”, from Customer Master) and stock items (Rates Master barcode, HSN, GST) in Tally by itself. For anything else WMS asks for, create it in Tally, then press “Re-read Tally masters”.',
];

/** Admin: Tally company + voucher types + the downloadable link script. */
export const TallySetupDialog = ({ open, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [busy, setBusy] = useState('');
  useEffect(() => {
    if (!open) return;
    setF(null);
    tallyApi.status().then((s) => { setF({ company: s.company, godown: s.godown, vch_types: s.vch_types, accounts: s.accounts, create: s.create }); setCompanies(s.tally_companies); })
      .catch((e) => toast.error(errMsg(e, 'Failed to load')));
  }, [open]);
  const save = () => {
    setBusy('save');
    tallyApi.saveSettings(f).then(() => { toast.success('Tally link settings saved'); onSaved(); })
      .catch((e) => toast.error(errMsg(e, 'Save failed'))).finally(() => setBusy(''));
  };
  const download = () => {
    setBusy('dl');
    tallyApi.downloadScript(window.location.origin).then(() => { toast.success('Tally link downloaded — any older copy stops working'); onSaved(); })
      .catch((e) => toast.error(errMsg(e, 'Download failed'))).finally(() => setBusy(''));
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="tally-setup-dialog">
        <DialogHeader><DialogTitle>Tally link setup</DialogTitle></DialogHeader>
        {!f ? <Loader2 className="w-6 h-6 mx-auto my-8 animate-spin text-amber-600" /> : (
          <div className="space-y-5">
            <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-700" data-testid="tally-setup-steps">{STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Tally company name (exactly as in Tally)</Label>
                <Input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} list="tally-companies" data-testid="tally-company-input" />
                <datalist id="tally-companies">{companies.map((c) => <option key={c} value={c} />)}</datalist></div>
              <div className="space-y-1"><Label>Godown for stock going out</Label>
                <Input value={f.godown} onChange={(e) => setF({ ...f, godown: e.target.value })} data-testid="tally-godown-input" /></div>
              {VT.map(([k, l]) => (
                <div key={k} className="space-y-1"><Label>{l} Tally voucher type</Label>
                  <Input value={f.vch_types[k] || ''} onChange={(e) => setF({ ...f, vch_types: { ...f.vch_types, [k]: e.target.value } })} data-testid={`tally-vt-${k}`} /></div>
              ))}
            </div>
            <div className="space-y-2 rounded-lg border border-gray-200 p-3" data-testid="tally-accounts-settings">
              <div className="text-sm font-semibold">Ledgers for “{f.vch_types.customer_invoice || 'Sales - EWB'}” (direct sales invoices)</div>
              <div className="grid md:grid-cols-3 gap-3">
                {ACC.map(([k, l]) => (
                  <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label>
                    <Input value={f.accounts?.[k] ?? ''} onChange={(e) => setF({ ...f, accounts: { ...f.accounts, [k]: e.target.value } })} data-testid={`tally-acc-${k}`} /></div>
                ))}
              </div>
            </div>
            <div className="space-y-2 rounded-lg border border-sky-200 bg-sky-50/40 p-3" data-testid="tally-create-settings">
              <div className="text-sm font-semibold">New masters WMS creates in Tally</div>
              <p className="text-xs text-gray-600">Customer ledgers are named “Name - Place”. Stock items get the Rates Master barcode as name, HSN, and GST 5% (TN WSP ≤ ₹2,500) or 18%. A missing stock group / unit is created too.</p>
              <div className="grid md:grid-cols-3 gap-3">
                {NEW.map(([k, l]) => (
                  <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label>
                    <Input value={f.create?.[k] ?? ''} onChange={(e) => setF({ ...f, create: { ...f.create, [k]: e.target.value } })} data-testid={`tally-create-${k}`} /></div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap justify-between gap-2 border-t pt-4">
              <Button variant="outline" onClick={download} disabled={!!busy} data-testid="tally-download-btn">
                {busy === 'dl' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Download Tally link (new key)
              </Button>
              <Button onClick={save} disabled={!!busy} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="tally-settings-save">
                {busy === 'save' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save settings
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TallySetupDialog;
